import { storage } from '../../utils/storage.js';
import { HangoutState } from '../state/HangoutState.js';
import { getVisibleMessages } from '../shared/message-tree.js';
import { estimateTokens } from './token-estimate.js';
import { getGlobalHistoryMessages } from '../shared/history-resolution.js';

const CHAT_MEMORY_KEY = 'hangout:chatMemory';
const CHARACTER_INFO_OVERRIDES_KEY = 'hangout:characterInfoOverrides';
export const CHAT_SUMMARY_MAX_LENGTH = 750;

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/**
 * @returns {{ text: string, summarizedCount: number, conversationId: string|null }}
 */
export const getChatMemory = () => {
  const stored = storage.get(CHAT_MEMORY_KEY);
  if (!isPlainObject(stored)) return { text: '', summarizedCount: 0, conversationId: null };
  const text = typeof stored.text === 'string' ? stored.text.slice(0, CHAT_SUMMARY_MAX_LENGTH) : '';
  const summarizedCount = Number.isFinite(Number(stored.summarizedCount))
    ? Math.max(0, Math.round(Number(stored.summarizedCount)))
    : 0;
  const conversationId = typeof stored.conversationId === 'string' && stored.conversationId.trim()
    ? stored.conversationId.trim()
    : null;
  return { text, summarizedCount, conversationId };
};

/**
 * @param {{ text?: string, summarizedCount?: number, conversationId?: string|null }} next
 */
export const setChatMemory = (next) => {
  const current = getChatMemory();
  const text = typeof next?.text === 'string' ? next.text.slice(0, CHAT_SUMMARY_MAX_LENGTH) : current.text;
  const summarizedCount = Number.isFinite(Number(next?.summarizedCount))
    ? Math.max(0, Math.round(Number(next.summarizedCount)))
    : current.summarizedCount;
  const conversationId = Object.prototype.hasOwnProperty.call(next || {}, 'conversationId')
    ? (typeof next.conversationId === 'string' && next.conversationId.trim() ? next.conversationId.trim() : null)
    : current.conversationId;
  if (!text.trim()) {
    const emptied = { text: '', summarizedCount: 0, conversationId };
    storage.set(CHAT_MEMORY_KEY, emptied);
    return emptied;
  }
  const saved = { text, summarizedCount, conversationId };
  storage.set(CHAT_MEMORY_KEY, saved);
  return saved;
};

const isSummarizableRole = (msg) => msg?.role === 'user' || msg?.role === 'assistant';

/**
 * Transcript messages used for pending-summary counts (user + assistant, active branch only).
 * @returns {Array<{ role?: string, content?: string }>}
 */
export const getSummarizableHistory = () => {
  const hangoutState = HangoutState.getInstance?.();
  const visibleFromState = hangoutState?.getVisibleMessages?.();
  if (Array.isArray(visibleFromState) && visibleFromState.length > 0) {
    return visibleFromState.filter(isSummarizableRole);
  }
  const stored = getGlobalHistoryMessages();
  if (stored.length === 0) return [];
  const activeVersions = hangoutState?.getActiveVersions?.() || {};
  const pendingBranch = hangoutState?.state?.pendingBranch || null;
  const branchTipPath = hangoutState?.getBranchTipPath?.() || null;
  const greetingVariation = hangoutState?.getGreetingVariationMeta?.()?.variation;
  return getVisibleMessages(stored, activeVersions, { pendingBranch, branchTipPath, greetingVariation }).filter(isSummarizableRole);
};

/**
 * @returns {{ messages: Array, count: number, tokens: number }}
 */
export const getPendingSummaryStats = () => {
  const { summarizedCount } = getChatMemory();
  const history = getSummarizableHistory();
  const pending = history.slice(Math.min(summarizedCount, history.length));
  const tokens = estimateTokens(pending.map((msg) => msg?.content || '').join('\n'));
  return { messages: pending, count: pending.length, tokens };
};

/**
 * @returns {Record<string, { values: Record<string, string>, removed: string[] }>}
 */
export const getAllCharacterInfoOverrides = () => {
  const stored = storage.get(CHARACTER_INFO_OVERRIDES_KEY);
  if (!isPlainObject(stored)) return {};
  /** @type {Record<string, { values: Record<string, string>, removed: string[] }>} */
  const next = {};
  for (const [id, entry] of Object.entries(stored)) {
    if (!isPlainObject(entry)) continue;
    const values = isPlainObject(entry.values)
      ? Object.fromEntries(
        Object.entries(entry.values)
          .filter(([, value]) => typeof value === 'string')
          .map(([key, value]) => [key, value])
      )
      : {};
    const removed = Array.isArray(entry.removed)
      ? entry.removed.map((key) => String(key)).filter(Boolean)
      : [];
    next[String(id)] = { values, removed };
  }
  return next;
};

const persistCharacterInfoOverrides = (all) => {
  storage.set(CHARACTER_INFO_OVERRIDES_KEY, all);
};

/**
 * @param {string|number} characterId
 * @returns {{ values: Record<string, string>, removed: string[] }}
 */
export const getCharacterInfoOverride = (characterId) => {
  const id = String(characterId ?? '');
  return getAllCharacterInfoOverrides()[id] || { values: {}, removed: [] };
};

/**
 * @param {string|number} characterId
 * @param {{ values?: Record<string, string>, removed?: string[] }} patch
 */
export const setCharacterInfoOverride = (characterId, patch) => {
  const id = String(characterId ?? '');
  if (!id) return;
  const all = getAllCharacterInfoOverrides();
  const current = all[id] || { values: {}, removed: [] };
  all[id] = {
    values: patch.values ? { ...patch.values } : current.values,
    removed: Array.isArray(patch.removed) ? [...new Set(patch.removed.map(String))] : current.removed
  };
  persistCharacterInfoOverrides(all);
};

/**
 * @param {string|number} characterId
 */
export const clearCharacterInfoOverride = (characterId) => {
  const id = String(characterId ?? '');
  const all = getAllCharacterInfoOverrides();
  delete all[id];
  persistCharacterInfoOverrides(all);
};

/**
 * Effective info object for display and /message (removed keys omitted).
 * @param {Record<string, unknown>|null|undefined} originalInfo
 * @param {string|number} characterId
 * @returns {Record<string, string>}
 */
export const getEffectiveCharacterInfo = (originalInfo, characterId, override = getCharacterInfoOverride(characterId)) => {
  const source = isPlainObject(originalInfo) ? originalInfo : {};
  const removed = new Set(override.removed);
  /** @type {Record<string, string>} */
  const effective = {};
  for (const [key, val] of Object.entries(source)) {
    if (removed.has(key)) continue;
    if (Object.prototype.hasOwnProperty.call(override.values, key)) {
      effective[key] = override.values[key];
      continue;
    }
    if (val === null || val === undefined || val === '') continue;
    if (typeof val === 'object' && !Array.isArray(val)) continue;
    if (Array.isArray(val) && val.length === 0) continue;
    const asString = Array.isArray(val)
      ? val
        .map((item) => (typeof item === 'string' ? item : item?.name ?? item))
        .filter((item) => item != null && String(item).trim() !== '')
        .map((item) => String(item).trim())
        .join(', ')
      : String(val);
    if (asString) effective[key] = asString;
  }
  for (const [key, value] of Object.entries(override.values)) {
    if (removed.has(key)) continue;
    effective[key] = value;
  }
  return effective;
};

/**
 * Map of characterId -> effective string fields for the worker.
 * @param {Array<{ id?: string|number }>} characters
 * @param {(id: string|number) => Record<string, unknown>|null|undefined} getOriginalInfo
 * @returns {Record<string, Record<string, string>>}
 */
export const buildCharacterInfoOverridesPayload = (characters, getOriginalInfo) => {
  const all = getAllCharacterInfoOverrides();
  /** @type {Record<string, Record<string, string>>} */
  const payload = {};
  for (const character of Array.isArray(characters) ? characters : []) {
    const id = character?.id;
    if (id == null) continue;
    const override = all[String(id)];
    if (!override) continue;
    payload[String(id)] = getEffectiveCharacterInfo(getOriginalInfo(id), id, override);
  }
  return payload;
};

/**
 * @param {Record<string, unknown>} requestBody
 * @param {Array<{ id?: string|number, info?: Record<string, unknown> }>} characters
 */
export const applyHangoutMemoryToRequestBody = (requestBody, characters = []) => {
  const { text, conversationId } = getChatMemory();
  const fromState = HangoutState.getInstance?.()?.getConversationId?.();
  const activeConversationId = typeof fromState === 'string' && fromState.trim() ? fromState.trim() : null;
  const isSameConversation = !conversationId || !activeConversationId || conversationId === activeConversationId;
  const trimmed = text.trim();
  if (trimmed && isSameConversation) requestBody.chatSummary = trimmed.slice(0, CHAT_SUMMARY_MAX_LENGTH);
  else delete requestBody.chatSummary;

  const roster = Array.isArray(characters) ? characters : [];
  const overrides = buildCharacterInfoOverridesPayload(
    roster,
    (id) => roster.find((character) => String(character.id) === String(id))?.info
  );
  if (Object.keys(overrides).length > 0) requestBody.characterInfoOverrides = overrides;
  else delete requestBody.characterInfoOverrides;
};
